// admin-web/src/pages/AuditLogsPage.tsx
import {
    collection,
    getDocs,
    limit,
    orderBy,
    query,
    Timestamp,
} from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";

type AuditLog = {
  id: string;

  type: string;
  actorUid?: string;
  actorEmail?: string;

  clubId?: string;
  threadId?: string;

  targetUid?: string;
  targetEmail?: string;

  meta?: Record<string, any>;

  createdAt?: Timestamp | null;
};

function formatTs(ts?: Timestamp | null) {
  if (!ts) return "";
  try {
    const d = ts.toDate();
    return d.toLocaleString();
  } catch {
    return "";
  }
}

function safeJson(v: any) {
  try {
    return JSON.stringify(v ?? {}, null, 2);
  } catch {
    return "";
  }
}

export function AuditLogsPage() {
  const [rows, setRows] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [filter, setFilter] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const qRef = query(collection(db, "auditLogs"), orderBy("createdAt", "desc"), limit(300));
      const snap = await getDocs(qRef);

      const out: AuditLog[] = snap.docs.map((d) => {
        const data = d.data() as any;
        return {
          id: d.id,
          type: data.type,
          actorUid: data.actorUid,
          actorEmail: data.actorEmail,
          clubId: data.clubId,
          threadId: data.threadId,
          targetUid: data.targetUid,
          targetEmail: data.targetEmail,
          meta: data.meta,
          createdAt: data.createdAt ?? null,
        };
      });

      setRows(out);
    } catch (e: any) {
      setErr(e?.message || "Failed to load audit logs.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return rows;

    return rows.filter((r) => {
      const hay = [
        r.type,
        r.actorEmail,
        r.actorUid,
        r.clubId,
        r.threadId,
        r.targetEmail,
        r.targetUid,
        safeJson(r.meta),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(f);
    });
  }, [rows, filter]);

  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h2 style={{ marginTop: 0 }}>Audit Logs</h2>
          <p className="muted" style={{ marginTop: 6 }}>
            Recent security-relevant actions (invites, acceptances, membership changes, thread changes).
          </p>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn secondary" onClick={load} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {err ? <div className="error">{err}</div> : null}

      <div className="grid2">
        <div>
          <label className="label">Filter</label>
          <input
            className="input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="type, email, uid, clubId, threadId…"
          />
        </div>
        <div>
          <label className="label">Showing</label>
          <div className="muted" style={{ paddingTop: 10 }}>
            {filtered.length} / {rows.length} (latest 300)
          </div>
        </div>
      </div>

      <hr className="hr" />

      {loading ? (
        <div className="muted">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="muted">No audit logs found.</div>
      ) : (
        <div className="table">
          <div className="thead" style={{ gridTemplateColumns: "190px 220px 1fr 220px 120px" }}>
            <div>Time</div>
            <div>Type</div>
            <div>Actor → Target</div>
            <div>Club / Thread</div>
            <div></div>
          </div>

          {filtered.map((r) => {
            const actor = r.actorEmail || r.actorUid || "—";
            const target = r.targetEmail || r.targetUid || "—";
            const clubThread = `${r.clubId ? `club:${r.clubId}` : ""}${r.clubId && r.threadId ? " • " : ""}${
              r.threadId ? `thread:${r.threadId}` : ""
            }`.trim() || "—";

            const expanded = expandedId === r.id;

            return (
              <div key={r.id} style={{ marginBottom: 10 }}>
                <div className="trow" style={{ gridTemplateColumns: "190px 220px 1fr 220px 120px" }}>
                  <div>
                    <div style={{ fontWeight: 800 }}>{formatTs(r.createdAt)}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{r.id}</div>
                  </div>

                  <div>
                    <span className="pill">{r.type}</span>
                  </div>

                  <div>
                    <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {actor} <span className="muted">→</span> {target}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {r.actorUid ? `actorUid: ${r.actorUid}` : ""}
                      {r.actorUid && r.targetUid ? " • " : ""}
                      {r.targetUid ? `targetUid: ${r.targetUid}` : ""}
                    </div>
                  </div>

                  <div className="muted" style={{ fontSize: 12 }}>
                    {clubThread}
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <button
                      className="btn small secondary"
                      onClick={() => setExpandedId(expanded ? null : r.id)}
                    >
                      {expanded ? "Hide" : "Details"}
                    </button>
                  </div>
                </div>

                {expanded ? (
                  <div className="card" style={{ marginTop: 8, background: "rgba(0,0,0,0.02)" }}>
                    <div className="grid2">
                      <div>
                        <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Meta</div>
                        <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{safeJson(r.meta)}</pre>
                      </div>
                      <div>
                        <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Raw</div>
                        <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                          {safeJson({
                            type: r.type,
                            actorEmail: r.actorEmail,
                            actorUid: r.actorUid,
                            targetEmail: r.targetEmail,
                            targetUid: r.targetUid,
                            clubId: r.clubId,
                            threadId: r.threadId,
                            createdAt: formatTs(r.createdAt),
                          })}
                        </pre>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
